"use client";

import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { useGSAP } from "@gsap/react";
import SectionTitle from "./section-title";
import { useTranslation } from "@/hooks/useTranslation";
import { useParams } from "next/navigation";
import { Locale, isRTLLanguage } from "@/lib/i18n/config";

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, DrawSVGPlugin);
}

type JourneyStep = {
  id: string;
  title: string;
  icon: string;
  description: string;
};

// This will be replaced with dynamic content

export default function JourneySection() {
  const { t, locale } = useTranslation('home');
  const params = useParams();
  const currentLocale = (params?.locale as Locale) || locale;
  const isRtl = isRTLLanguage(currentLocale);
  
  const journeySectionRef = useRef<HTMLElement>(null);
  const journeyPathRef = useRef<SVGPathElement>(null);
  const journeyBackgroundPathRef = useRef<SVGPathElement>(null);
  // const walkerRef = useRef<HTMLDivElement>(null); // Commented out walkerRef
  const journeyIconRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isMounted, setIsMounted] = useState(false);

  const journeySteps: JourneyStep[] = [
    {
      id: "consultation",
      title: t('journey.steps.consultation.title'),
      icon: "/images/icons/consultation.svg",
      description: t('journey.steps.consultation.description'),
    },
    {
      id: "scan",
      title: t('journey.steps.scan.title'),
      icon: "/images/icons/scan.svg",
      description: t('journey.steps.scan.description'),
    },
    {
      id: "plan",
      title: t('journey.steps.plan.title'),
      icon: "/images/icons/plan.svg",
      description: t('journey.steps.plan.description'),
    },
    {
      id: "treatment",
      title: t('journey.steps.treatment.title'),
      icon: "/images/icons/implant.svg",
      description: t('journey.steps.treatment.description'),
    },
    {
      id: "reveal",
      title: t('journey.steps.reveal.title'),
      icon: "/images/icons/smile.svg",
      description: t('journey.steps.reveal.description'),
    },
  ];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
  }, [isMounted]);

  // Use GSAP for journey animations with the useGSAP hook for cleaner setup
  useGSAP(() => {
    if (!isMounted || typeof window === "undefined") return;

    if (
      !journeySectionRef.current ||
      !journeyPathRef.current ||
      !journeyBackgroundPathRef.current // || // Commented out walkerRef null check
      // !walkerRef.current // Commented out walkerRef null check
    )
      return;

    // Set journey icons refs array to the correct length
    journeyIconRefs.current = journeyIconRefs.current.slice(
      0,
      journeySteps.length
    );

    // Create the main timeline
    const journeyTL = gsap.timeline({
      scrollTrigger: {
        trigger: journeySectionRef.current,
        start: "top 100%",
        end: "bottom bottom-=100",
        scrub: 1.5,
        markers: false,
      },
    });

    // Animate the background path drawing slightly ahead with 0.6s delay
    journeyTL.fromTo(
      journeyBackgroundPathRef.current,
      { drawSVG: "0%" },
      { drawSVG: "100%", ease: "none", duration: 1 },
      0.6
    );

    // Animate the main path drawing
    journeyTL.fromTo(
      journeyPathRef.current,
      { drawSVG: "0%" },
      { drawSVG: "100%", ease: "none", duration: 1 },
      0.65
    );

    // // Animate the walker along the path // Commented out walker animation block
    // journeyTL.to(
    //   walkerRef.current,
    //   {
    //     motionPath: {
    //       path: journeyPathRef.current,
    //       align: journeyPathRef.current,
    //       alignOrigin: [0.5, 0.5],
    //       autoRotate: true,
    //     },
    //     ease: "none",
    //     duration: 1,
    //   },
    //   0.65
    // );

    // Add step animations
    journeySteps.forEach((_, index) => {
      const iconEl = journeyIconRefs.current[index];
      if (!iconEl) return;

      const progress = index / (journeySteps.length - 1);
      const animationStartTime = progress * 0.85 + 0.6 + 0.1;
      const isEven = index % 2 === 0;

      gsap.set(iconEl, {
        xPercent: isEven ? -10 : 10,
        autoAlpha: 0,
        scale: 0.7,
        y: 20,
      });

      journeyTL.to(
        iconEl,
        {
          autoAlpha: 1,
          scale: 1,
          y: 0,
          xPercent: 0,
          ease: "back.out(1.7)",
          duration: 0.3,
        },
        animationStartTime
      );
    });
  }, [isMounted]); // Add isMounted as a dependency

  if (!isMounted) {
    return (
      <section className="relative pt-20 pb-0 min-h-[132vh] bg-gradient-to-b from-background to-muted/50 overflow-hidden">
        <div className="container mx-auto px-4">
          <SectionTitle>{t('journey.title')}</SectionTitle>
          <div className="relative mt-20 flex justify-center h-[120vh]">
            {/* Placeholder content */}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={journeySectionRef}
      className="relative pt-20 pb-0 min-h-[132vh] bg-gradient-to-b from-background to-muted/50 overflow-hidden"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4">
        <SectionTitle>{t('journey.title')}</SectionTitle>

        <div className="relative mt-20 flex justify-center h-[120vh]">
          <div className="relative w-full max-w-4xl">
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 400 800"
              preserveAspectRatio="none"
            >
              <path
                ref={journeyBackgroundPathRef}
                d="M200,0 C310,120 90,220 250,350 C380,480 20,580 150,680 C280,780 150,900 200,900"
                fill="none"
                stroke="#e0e7ff"
                strokeWidth="30"
                strokeLinecap="round"
                className="opacity-30"
              />
              <path
                ref={journeyPathRef}
                d="M200,0 C310,120 90,220 250,350 C380,480 20,580 150,680 C280,780 150,900 200,900"
                fill="none"
                stroke="url(#journeyGradient)"
                strokeWidth="6"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient
                  id="journeyGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#4f46e5" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
              </defs>
            </svg>

            {/* <div // Commented out walker div
              ref={walkerRef}
              className="absolute top-0 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
            >
              <img
                src="/images/icons/walker-left.svg"
                alt="Journey Walker"
                className="w-10 h-14 origin-bottom"
              />
            </div> */}

            {journeySteps.map((step, index) => {
              let positionStyle = {};
              let isLeft = false;

              switch (step.id) {
                case "consultation":
                  positionStyle = { top: "15%", right: "-5%" };
                  isLeft = true;
                  break;
                case "scan":
                  positionStyle = isRtl ? { top: "35%", left: "-4%" } : { top: "35%", left: "-4%" };
                  isLeft = isRtl ? true : false;
                  break;
                case "plan":
                  positionStyle = { top: "50%", right: "-10%" };
                  isLeft = true;
                  break;
                case "treatment":
                  positionStyle = isRtl ? { top: "80%", left: "-10%" } : { top: "80%", left: "-10%" };
                  isLeft = isRtl ? true : false;
                  break;
                case "reveal":
                  positionStyle = { top: "93%", right: "-6%" };
                  isLeft = true;
                  break;
                default:
                  positionStyle = {
                    top: `${(index / (journeySteps.length - 1)) * 100}%`,
                  };
                  isLeft = index % 2 === 0;
              }

              return (
                <div
                  key={step.id}
                  ref={(el) => {
                    journeyIconRefs.current[index] = el;
                  }}
                  className="absolute w-64 sm:w-72 md:w-80 opacity-0 pointer-events-none z-10"
                  style={{
                    ...positionStyle,
                    transform: "translateY(-50%)",
                  }}
                >

                  <div
                    className={`flex flex-col items-center text-center gap-3 sm:gap-4 sm:items-center ${(isLeft && !isRtl) || (!isLeft && isRtl)
                      ? "sm:flex-row sm:text-left sm:pr-4"
                      : "sm:flex-row-reverse sm:text-right sm:pl-4"
                      }`}
                  >
                    <div className="relative shrink-0">
                      <div className="absolute -inset-2 bg-primary/20 rounded-full animate-pulse" />
                      <div className="relative z-10 rounded-full bg-white p-2 sm:p-3 shadow-lg border-2 border-primary">
                        <img
                          src={step.icon}
                          alt={step.title}
                          className="w-8 h-8 sm:w-10 sm:h-10"
                        />
                      </div>
                    </div>
                    <div className="mt-1 sm:mt-0 w-[180px] sm:w-full">
                      <h3 className="text-sm sm:text-xl font-semibold mb-1 line-clamp-1">
                        {step.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
